import type { EmailRecipient, SchedulingOption } from '@/components/email-send-dialog';
import type { Template } from '@/types/email';
import { DEFAULT_OFFICE_LOCATION, OFFICE_LOCATIONS, buildOfficeFooter } from '@/constants/office-locations';

type SchedulePayload =
  | { kind: 'preset'; option: SchedulingOption; scheduledAt: string }
  | { kind: 'custom'; scheduledAt: string };


type PlaceholderContext = {
  firstName?: string | null;
  lastName?: string | null;
  recipientName?: string | null;
  jobTitle?: string | null;
  vacancyName?: string | null;
  company?: string | null;
  companyLocation?: string | null;
  responseDeadlineReadable?: string | null;
  responseDeadlineISO?: string | null;
  interviewDateReadable?: string | null;
  interviewDateISO?: string | null;
  interviewTime?: string | null;
  interviewLocation?: string | null;
  recruiterName?: string | null;
  senderEmail?: string | null;
  candidateEmail?: string | null;
  primaryRecipientEmail?: string | null;
  hideCompanyFallback?: boolean;
  location?: string | null;
  phoneNumber?: string | null;
  tel?: string | null;
  reception?: string | null;
  Advisor?: string | null;
  AdvisorOrOffice?: string | null;
  officeFooter?: string | null;
};

type SendCandidateInviteArgs = {
  template: Template;
  templateId?: number | null;
  body: string;
  recipients: EmailRecipient[];
  candidateName?: string | null;
  jobTitle?: string | null;
  company?: string | null;
  applicationId?: string | number | null;
  applicationKey?: string | null;
  schedule?: SchedulePayload | null;
  subject?: string | null;
  fromEmail?: string | null;
  actor?: {
    nickname?: string | null;
    name?: string | null;
    email?: string | null;
    userId?: string | null;
  } | null;
  markers?: Record<string, string> | null;
  accessToken: string;
  hideCompanyInEmail?: boolean;
};

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL ?? '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY ?? '';
const ENQUEUE_MAIL_ENDPOINT = SUPABASE_URL
  ? `${SUPABASE_URL}/functions/v1/enqueue-mail`
  : '';


const placeholderSamples: Record<string, (context: PlaceholderContext) => string> = {
  firstName: (context) => context.firstName ?? 'Kandidaat',
  lastName: (context) => context.lastName ?? 'Achternaam',
  recipientName: (context) => context.recipientName ?? 'Kandidaat',
  jobTitle: (context) => context.jobTitle ?? 'Functietitel',
  role: (context) => context.jobTitle ?? 'Functietitel',
  company: (context) => {
    if (context.hideCompanyFallback) {
      return context.company ?? '';
    }
    return context.company ?? 'Siebert & Wassink';
  },
  companyLocation: (context) => context.companyLocation ?? '',
  vacancyName: (context) => context.vacancyName ?? context.jobTitle ?? 'Vacaturetitel',
  responseDeadline: (context) => context.responseDeadlineReadable ?? 'Binnen 3 dagen',
  responseDeadlineISO: (context) => context.responseDeadlineISO ?? new Date().toISOString(),
  interviewDate: (context) => context.interviewDateReadable ?? 'Aanstaande maandag',
  interviewDateISO: (context) => context.interviewDateISO ?? new Date().toISOString(),
  interviewTime: (context) => context.interviewTime ?? '09:00',
  interviewLocation: (context) => context.interviewLocation ?? 'Hoofdkantoor',
  recruiterName: (context) => context.recruiterName ?? 'Recruitmentteam Siebert & Wassink',
  senderEmail: (context) => context.senderEmail ?? 'office@siebertwassink.nl',
  candidateEmail: (context) => context.candidateEmail ?? context.primaryRecipientEmail ?? 'kandidaat@example.com',
  Locatie: (context) =>
    context.location ?? OFFICE_LOCATIONS[DEFAULT_OFFICE_LOCATION].address,
  phoneNumber: (context) =>
    context.phoneNumber ?? OFFICE_LOCATIONS[DEFAULT_OFFICE_LOCATION].phone,
  tel: (context) =>
    context.tel ?? OFFICE_LOCATIONS[DEFAULT_OFFICE_LOCATION].tel,
  reception: (context) => context.reception ?? '',
  Advisor: (context) => context.Advisor ?? '',
  AdvisorOrOffice: (context) => context.AdvisorOrOffice ?? '',
  officeFooter: (context) => context.officeFooter ?? buildOfficeFooter(DEFAULT_OFFICE_LOCATION),
}

const allowedMarkerKeys = new Set(Object.keys(placeholderSamples));

const sanitizeMarkerMap = (input?: Record<string, string> | null) => {
  if (!input) {
    return {};
  }
  return Object.entries(input).reduce<Record<string, string>>((acc, [rawKey, rawValue]) => {
    const key = (rawKey ?? '').toString().trim();
    if (!key || !allowedMarkerKeys.has(key)) {
      return acc;
    }
    const value = (rawValue ?? '').toString().trim();
    if (!value) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
};

const resolveApplicationIdentifiers = (value?: string | number | null) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return { numericId: value, applicationKey: null as string | null };
  }
  if (typeof value === 'string' && value.trim().length > 0) {
    const trimmed = value.trim();
    if (/^\d+$/.test(trimmed)) {
      return { numericId: Number.parseInt(trimmed, 10), applicationKey: null as string | null };
    }
    return { numericId: null as number | null, applicationKey: trimmed };
  }
  return { numericId: null as number | null, applicationKey: null as string | null };
};

const buildPlaceholderValues = (context: PlaceholderContext) =>
  Object.fromEntries(
    Object.entries(placeholderSamples).map(([key, resolver]) => [key, resolver(context)])
  ) as Record<string, string>;

const stripCompanyPlaceholder = (input: string) =>
  input
    .replace(/\s*\bbij\s*\{\{\s*company\s*\}\}(\.)?/gi, (_match, dot) => (dot ? '.' : ''))
    .replace(/\s{2,}/g, ' ')
    .trim();

/**
 * 
 * @returns idemopotency key for the resend email
 */
const createIdempotencyKey = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
};

const splitName = (input?: string | null) => {
  if (!input) {
    return { first: null, last: null };
  }
  const segments = input.trim().split(/\s+/);
  if (segments.length === 0) {
    return { first: null, last: null };
  }
  const [first, ...rest] = segments;
  return {
    first: first ?? null,
    last: rest.length ? rest.join(' ') : null,
  };
};

/**
 * Send a candidate invite email.
 * @param SendCandidateInviteArgs The parameters for sending the email.
 * @returns A promise that resolves when the email has been sent.
 */
export const sendCandidateInvite = async ({
  template,
  templateId = null,
  body,
  recipients,
  candidateName,
  jobTitle,
  company,
  applicationId,
  applicationKey,
  schedule,
  subject,
  fromEmail = null,
  actor = null,
  markers = null,
  accessToken,
  hideCompanyInEmail = false,
}: SendCandidateInviteArgs) => {
  if (!ENQUEUE_MAIL_ENDPOINT || !SUPABASE_ANON_KEY) {
    const error = new Error('Supabase configuratie ontbreekt voor e-mailverzending');
    console.error('[email] Failed to enqueue candidate invite:', error.message);
    throw error;
  }
  if (!accessToken) {
    throw new Error("Missing Auth0 access token for enqueue-mail request");
  }

  if (recipients.length === 0) {
    const error = new Error('Geen ontvanger beschikbaar voor deze e-mail');
    console.error('[email] Failed to enqueue candidate invite:', error.message);
    throw error;
  }

  const normalizedRecipients = recipients
    .map((recipient) => {
      const email = recipient.email?.trim();
      if (!email) {
        return null;
      }
      return {
        ...recipient,
        email,
        name:
          recipient.name && recipient.name.trim().length > 0
            ? recipient.name.trim()
            : recipient.name ?? email,
      };
    })
    .filter(Boolean) as EmailRecipient[];

  if (!normalizedRecipients.length) {
    const error = new Error('Geen geldige ontvangers gevonden voor deze e-mail');
    console.error('[email] Failed to enqueue candidate invite:', error.message);
    throw error;
  }

  const actorDisplayName = actor?.nickname ?? actor?.name ?? null;
  const actorEmail = actor?.email ?? null;
  const actorUserId = actor?.userId ?? null;
  const resolvedFromEmail =
    typeof fromEmail === 'string' && fromEmail.trim().length > 0
      ? fromEmail.trim()
      : actorEmail ?? null;
  const globalMarkerOverrides = sanitizeMarkerMap(markers);
  if (hideCompanyInEmail) {
    if ('company' in globalMarkerOverrides) {
      delete globalMarkerOverrides.company;
    }
    if ('companyLocation' in globalMarkerOverrides) {
      delete globalMarkerOverrides.companyLocation;
    }
  }
  const globalApplicationIds = resolveApplicationIdentifiers(applicationId ?? null);
  const globalApplicationKey =
    applicationKey ??
    globalApplicationIds.applicationKey ??
    (typeof applicationId === 'string' && !/^\d+$/.test(applicationId ?? '')
      ? applicationId?.trim() ?? null
      : null);

  const recipientPayloads = normalizedRecipients.map((recipient) => {
    const mergedOverrides = {
      ...globalMarkerOverrides,
      ...sanitizeMarkerMap(recipient.markers),
    };
    if (hideCompanyInEmail) {
      if ('company' in mergedOverrides) {
        delete mergedOverrides.company;
      }
      if ('companyLocation' in mergedOverrides) {
        delete mergedOverrides.companyLocation;
      }
    }
    const displayName =
      recipient.name && recipient.name.trim().length > 0
        ? recipient.name.trim()
        : candidateName?.trim() ?? recipient.email;
    const { first, last } = splitName(displayName);
    const resolvedSender = (mergedOverrides.senderEmail ?? resolvedFromEmail ?? 'office@siebertwassink.nl') as string | null;
    const normalizedSender = typeof resolvedSender === 'string' ? resolvedSender.trim().toLowerCase() : '';

    // Compute AdvisorOrOffice: explicit override > Advisor value > special-case office sender
    let computedAdvisorOrOffice: string | null = mergedOverrides.AdvisorOrOffice ?? null;
    if (normalizedSender === 'office@siebertwassink.nl') {
      computedAdvisorOrOffice = 'Team office';
    } else if (!computedAdvisorOrOffice && mergedOverrides.Advisor) {
      computedAdvisorOrOffice = mergedOverrides.Advisor;
    }

    const context: PlaceholderContext = {
      firstName: mergedOverrides.firstName ?? first,
      lastName: mergedOverrides.lastName ?? last,
      recipientName: mergedOverrides.recipientName ?? displayName,
      jobTitle: mergedOverrides.jobTitle ?? jobTitle ?? null,
      vacancyName: mergedOverrides.vacancyName ?? jobTitle ?? null,
      company: hideCompanyInEmail ? null : mergedOverrides.company ?? company ?? null,
      companyLocation: hideCompanyInEmail ? null : mergedOverrides.companyLocation ?? null,
      responseDeadlineReadable: mergedOverrides.responseDeadline ?? null,
      responseDeadlineISO: mergedOverrides.responseDeadlineISO ?? null,
      interviewDateReadable: mergedOverrides.interviewDate ?? null,
      interviewDateISO: mergedOverrides.interviewDateISO ?? null,
      interviewTime: mergedOverrides.interviewTime ?? null,
      interviewLocation: mergedOverrides.interviewLocation ?? null,
      recruiterName: mergedOverrides.recruiterName ?? actorDisplayName ?? 'Recruitmentteam Siebert & Wassink',
      senderEmail: resolvedSender,
      Advisor: mergedOverrides.Advisor ?? null,
      AdvisorOrOffice: computedAdvisorOrOffice ?? null,
      candidateEmail: mergedOverrides.candidateEmail ?? recipient.email,
      primaryRecipientEmail: recipient.email,
      hideCompanyFallback: hideCompanyInEmail,
      location: mergedOverrides.Locatie ?? null,
      phoneNumber: mergedOverrides.phoneNumber ?? null,
      tel: mergedOverrides.tel ?? null,
      reception: mergedOverrides.reception ?? null,
      officeFooter: mergedOverrides.officeFooter ?? null,
    };
    const defaultMarkers = buildPlaceholderValues(context);
    const markerPayload = {
      ...defaultMarkers,
      ...mergedOverrides,
    };

    const identifiers = resolveApplicationIdentifiers(
      recipient.applicationId ?? applicationId ?? null
    );
    const applicationIdForRecipient =
      identifiers.numericId ?? globalApplicationIds.numericId ?? null;
    const applicationKeyForRecipient =
      recipient.applicationKey ?? identifiers.applicationKey ?? globalApplicationKey ?? null;

    return {
      recipient,
      markers: markerPayload,
      applicationId: applicationIdForRecipient,
      applicationKey: applicationKeyForRecipient,
      vacancyId: recipient.vacancyId ?? null,
      candidateId: recipient.candidateId ?? null,
      idempotencyKey: createIdempotencyKey(),
      vacatureNaam: recipient.vacatureNaam ?? mergedOverrides.vacancyName ?? null,
    };
  });

  if (!recipientPayloads.length) {
    const error = new Error('Geen ontvanger payloads opgebouwd');
    console.error('[email] Failed to enqueue candidate invite:', error.message);
    throw error;
  }

  const subjectOverride =
    typeof subject === 'string' && subject.trim().length > 0 ? subject.trim() : null;
  const messageBody = typeof body === 'string' ? body : '';
  const sanitizedSubject =
    hideCompanyInEmail && subjectOverride ? stripCompanyPlaceholder(subjectOverride) : subjectOverride;
  const sanitizedBody = hideCompanyInEmail ? stripCompanyPlaceholder(messageBody) : messageBody;
  const primaryPayload = recipientPayloads[0];

  const requestBody = {
    template_name: template,
    ...(templateId !== null ? { template_id: templateId } : {}),
    ...(sanitizedSubject ? { subject_override: sanitizedSubject } : {}),
    ...(primaryPayload.applicationId ? { application_id: primaryPayload.applicationId } : {}),
    ...(primaryPayload.applicationKey ? { application_key: primaryPayload.applicationKey } : {}),
    payload: {
      body: sanitizedBody,
      recipients: recipientPayloads.map((payload) => ({
        id: payload.recipient.id,
        name: payload.recipient.name,
        email: payload.recipient.email,
        markers: payload.markers,
        applicationId: payload.applicationId,
        applicationKey: payload.applicationKey,
        vacancyId: payload.vacancyId,
        candidateId: payload.candidateId,
        idempotencyKey: payload.idempotencyKey,
        vacatureNaam: payload.vacatureNaam,
      })),
      vacatureNaam: recipientPayloads[0]?.vacatureNaam ?? null,
      ...(schedule ? { schedule } : {}),
      actor: {
        name: actorDisplayName,
        email: actorEmail ?? null,
        userId: actorUserId ?? null,
      },
      senderEmail: resolvedFromEmail,
    },
  };

  try {
    const response = await fetch(ENQUEUE_MAIL_ENDPOINT, {
      method: 'POST',
      headers: {
        'content-type': 'application/json',
        apikey: SUPABASE_ANON_KEY,
        Authorization: `Bearer ${SUPABASE_ANON_KEY}`,
        'x-auth-token': `Bearer ${accessToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    const result = (await response.json().catch(() => null)) as
      | { id?: number; ids?: number[]; error?: unknown }
      | null;

    if (!response.ok) {
      const message =
        typeof result?.error === 'string'
          ? result.error
          : (result?.error as { message?: string } | undefined)?.message ??
            `HTTP ${response.status}`;
      throw new Error(message);
    }

    const queueIds = Array.isArray(result?.ids)
      ? result?.ids.filter((entry): entry is number => typeof entry === 'number')
      : typeof result?.id === 'number'
        ? [result.id]
        : [];
    console.log('[email] Candidate invite queued:', queueIds);
    return queueIds;
  } catch (caught) {
    const error =
      caught instanceof Error
        ? caught
        : new Error('Unknown error while queuing candidate invite');
    console.error('[email] Failed to enqueue candidate invite:', error.message);
    throw error;
  }
};

export type { SchedulePayload, SendCandidateInviteArgs };
